import { useCallback, useMemo, useState } from "react";

type FieldProps = {
  validateStatus?: "error";
  help?: string;
};

/**
 * Per-field validation for the hand-rolled automation forms.
 *
 * The whole yup schema is re-validated on every change so the Submit button
 * knows whether the form is complete, but a message is only surfaced for a
 * field the user has already blurred. Opening a blank modal therefore shows no
 * errors at all, and each one appears next to the field it belongs to as the
 * user leaves it.
 *
 * @param schema the yup object schema to validate against
 * @param value  the form state; a new object identity re-runs validation
 */
const useFieldErrors = (schema: any, value: any) => {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const { errors, formError } = useMemo(() => {
    const errors: Record<string, string> = {};
    let formError: string | null = null;

    try {
      // abortEarly: false so every field reports, not just the first failure.
      schema.validateSync(value, { abortEarly: false });
    } catch (e: any) {
      // `inner` is empty when yup throws a single, non-aggregated error.
      for (const err of e.inner?.length ? e.inner : [e]) {
        if (!err.path) {
          // Not attributable to a field — keep it so the form still counts as
          // invalid rather than silently enabling Submit.
          formError = formError ?? err.message;
        } else if (!errors[err.path]) {
          errors[err.path] = err.message;
        }
      }
    }

    return { errors, formError };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /** Mark one or more paths as blurred, so their errors may now be shown. */
  const touch = useCallback((...paths: string[]) => {
    setTouched((prev) =>
      paths.every((path) => prev[path])
        ? prev
        : { ...prev, ...Object.fromEntries(paths.map((path) => [path, true])) }
    );
  }, []);

  /**
   * antd `Form.Item` props for the control at `paths`. More than one path may
   * be given when a single control is responsible for several values — the
   * transactional List dropdown sets both `list_id` and `brand`, so an error on
   * either has to point back at that one dropdown.
   */
  const fieldProps = useCallback(
    (...paths: string[]): FieldProps => {
      const failing = paths.find((path) => touched[path] && errors[path]);
      return failing ? { validateStatus: "error", help: errors[failing] } : {};
    },
    [touched, errors]
  );

  return {
    isValid: !formError && Object.keys(errors).length === 0,
    touch,
    fieldProps,
  };
};

export default useFieldErrors;
